/**
 * Getting one small string to the ingest endpoint, from a page that may be
 * closing, on a network that may refuse.
 */

/**
 * Three transports, tried in order, and none of them may throw.
 *
 * ## 1. `navigator.sendBeacon`, and the `text/plain` that makes it work
 *
 * The brief asks for `sendBeacon` so that nothing is lost when the page goes
 * away, and it is the reason a named event fired in a link's click handler
 * arrives at all: a `fetch` started there is cancelled by the navigation that
 * follows it, and `sendBeacon` is handed to the browser's own queue and
 * survives the document.
 *
 * The body is a `Blob` typed `text/plain`, NOT `application/json`, and this is
 * the detail that decides whether the feature works at all. A cross-origin POST
 * with a JSON content type is not a "simple" CORS request, so the browser sends
 * a preflight `OPTIONS` first — and a preflight cannot complete on a document
 * that is unloading. The beacon would be dropped precisely in the case it
 * exists for, silently, and only for the last event of each session, which is
 * the hardest possible shape of bug to notice. `text/plain` is CORS-safelisted;
 * the POST goes straight out. The ingest route pays for this by having to
 * register a parser for that content type, which is written down in
 * `@panel/shared/tracker` where the route's author will read it.
 *
 * `sendBeacon` cannot be told to omit credentials, unlike the two fallbacks
 * below. It does not matter here and it is worth knowing why rather than
 * assuming: it would send cookies scoped to the INGEST origin, and Panel sets
 * no cookie on any origin, so there are none to send. If that ever stops being
 * true this comment is the thing that is wrong.
 *
 * ## 2. `fetch` with `keepalive`
 *
 * For the browsers and the contexts where `sendBeacon` is missing or returns
 * false — it returns false rather than throwing when the browser's beacon queue
 * is over its (64 KB, shared) limit, which a page with several other beacons
 * can reach. `keepalive` gives the same survive-the-unload property.
 *
 * ## 3. `XMLHttpRequest`
 *
 * The floor. It does not survive an unload, and it is here so that a page view
 * still counts on an engine that has neither of the above rather than being
 * lost to a feature check.
 *
 * ## Why nothing reads the response
 *
 * There is no answer worth having. The endpoint returns 204 to everything it
 * will ever return — accepted, over budget, malformed, unknown key — so there
 * is no state to react to, and a retry on a stranger's page would amplify
 * exactly the loop a budget exists to stop. A `.catch` that does nothing is not
 * laziness here; it is the contract.
 */
export function send(win: Window, endpoint: string, body: string): void {
  try {
    const nav = win.navigator;
    if (nav && typeof nav.sendBeacon === 'function') {
      if (nav.sendBeacon(endpoint, new Blob([body], { type: 'text/plain' }))) return;
    }
  } catch {
    /* fall through */
  }

  try {
    if (typeof win.fetch === 'function') {
      const pending = win.fetch(endpoint, {
        method: 'POST',
        body,
        mode: 'cors',
        keepalive: true,
        // Explicit, and a statement rather than a default: this request carries
        // no cookie in either direction, on any origin, ever.
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain' },
      });
      // A rejected promise with no handler is an unhandled rejection, which
      // lands a red line in the customer's console for a network error that is
      // none of their business.
      if (pending && typeof pending.catch === 'function') pending.catch(noop);
      return;
    }
  } catch {
    /* fall through */
  }

  try {
    const XHR = (win as Window & { XMLHttpRequest?: typeof XMLHttpRequest }).XMLHttpRequest;
    if (!XHR) return;
    const xhr = new XHR();
    xhr.open('POST', endpoint, true);
    xhr.withCredentials = false;
    xhr.setRequestHeader('Content-Type', 'text/plain');
    // Without this an error event on the request reaches `window.onerror` in
    // some engines, and the customer sees our failure as theirs.
    xhr.onerror = noop;
    xhr.send(body);
  } catch {
    /* Three transports refused. There is nothing left to try and nothing to
       report to, and the one thing this script must never do is make noise in
       somebody else's page. */
  }
}

function noop(): void {
  /* deliberately empty — see the header */
}
